<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveAdminOrdersStore } from '../../lib/convexRealtime';
  import { refundOrder } from '../../lib/convexApi';
  import type { AdminOrderRecord } from '../../lib/types';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let eventId: string;
  export let eventName: string;

  let store: ReturnType<typeof liveAdminOrdersStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedId: string | null = null;

  let orders: AdminOrderRecord[] = [];
  let loading = true;
  let error: string | null = null;

  let statusFilter: 'all' | 'completed' | 'refunded' | 'pending' | 'expired' | 'released' | 'committed' = 'all';

  let refundArmedId: string | null = null;
  let refundPendingId: string | null = null;
  let refundError: string | null = null;

  function canRefund(order: AdminOrderRecord): boolean {
    return order.kind === 'checkout' && order.status === 'completed' && Boolean(order.payment.paymentIntent);
  }

  async function handleRefund(order: AdminOrderRecord) {
    // Two-step: first click arms, second click executes.
    if (refundArmedId !== order.id) {
      refundArmedId = order.id;
      refundError = null;
      return;
    }
    refundArmedId = null;
    refundPendingId = order.id;
    refundError = null;
    try {
      await refundOrder(order.id);
      // The live orders subscription picks up the refunded status by itself.
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Refund failed.';
      refundError = raw
        .replace(/\[CONVEX[^\]]*\]\s*(\[Request ID:[^\]]*\]\s*)?Server Error\s*(Uncaught Error:)?\s*/i, '')
        .trim() || 'Refund failed.';
    } finally {
      refundPendingId = null;
    }
  }

  $: if (eventId && eventId !== subscribedId) {
    subscribedId = eventId;
    statusFilter = 'all';
    unsubscribe?.();
    store?.dispose();
    store = liveAdminOrdersStore({ eventId, limit: 200 });
    unsubscribe = store.subscribe(state => {
      orders = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
  }

  onDestroy(() => {
    unsubscribe?.();
    store?.dispose();
  });

  $: filtered = statusFilter === 'all' ? orders : orders.filter(order => order.status === statusFilter);
  $: totalGross = filtered.reduce((sum, order) => sum + (order.status === 'completed' ? order.payment.amount ?? 0 : 0), 0);
  $: currency = filtered.find(order => order.payment.amount != null)?.payment.currency ?? 'EUR';

  const STATUS_OPTIONS = ['all', 'completed', 'refunded', 'pending', 'expired', 'released', 'committed'] as const;

  function formatDate(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? iso
      : date.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function formatAmount(order: AdminOrderRecord): string {
    if (order.payment.amount == null) return '—';
    return `${order.payment.amount.toFixed(2)} ${order.payment.currency}`;
  }

  function expiryLabel(order: AdminOrderRecord): string | null {
    const ms = order.reservation?.expiresInMs ?? null;
    if (ms == null || order.status !== 'pending') return null;
    const minutes = Math.round(Math.abs(ms) / 60000);
    return ms > 0 ? `expires in ${minutes}m` : `expired ${minutes}m ago`;
  }
</script>

<section class="orders surface surface--sunken" aria-label={`Orders for ${eventName}`}>
  <header class="orders__header">
    <h4>Orders · {eventName}</h4>
    <span class="orders__summary">
      {filtered.length} entr{filtered.length === 1 ? 'y' : 'ies'}
      {#if totalGross > 0}
        · {totalGross.toFixed(2)} {currency} completed
      {/if}
    </span>
  </header>

  <div class="orders__chips" role="group" aria-label="Status filter">
    {#each STATUS_OPTIONS as option (option)}
      <button
        type="button"
        class="orders__chip"
        class:orders__chip--active={statusFilter === option}
        on:click={() => (statusFilter = option)}
      >
        {option === 'all' ? 'All' : option}
      </button>
    {/each}
  </div>

  {#if refundError}
    <Notice variant="error">{refundError}</Notice>
  {/if}

  {#if loading && orders.length === 0}
    <LoadingBlock label="Loading orders…" />
  {:else if error}
    <Notice variant="error">Could not load orders: {error}</Notice>
  {:else if filtered.length === 0}
    <p class="orders__empty">No orders {statusFilter === 'all' ? 'yet' : `with status "${statusFilter}"`}.</p>
  {:else}
    <ul>
      {#each filtered as order (order.id)}
        <li class="orders__row">
          <div class="orders__main">
            <span class="orders__who">
              {order.person?.name ?? 'Unknown'}
              <span class="orders__status orders__status--{order.status}">{order.status}</span>
            </span>
            <span class="orders__meta">
              {formatDate(order.createdAt)}
              · {order.kind === 'checkout' ? 'Stripe checkout' : 'Reservation'}
              {#if order.ticket}
                · {order.ticket.code}
              {/if}
              {#if expiryLabel(order)}
                · {expiryLabel(order)}
              {/if}
            </span>
            {#if order.extras.length > 0}
              <span class="orders__extras">
                {order.extras.map(extra => `${extra.quantity}× ${extra.sku}`).join(', ')}
              </span>
            {/if}
            {#if order.refund}
              <span class="orders__extras">
                Refunded{order.refund.amount != null ? ` ${order.refund.amount.toFixed(2)} ${order.refund.currency ?? ''}` : ''}
                · {formatDate(order.refund.at)}
              </span>
            {/if}
          </div>
          <div class="orders__side">
            <span class="orders__amount">{formatAmount(order)}</span>
            {#if canRefund(order)}
              <button
                type="button"
                class="ghost orders__refund"
                class:orders__refund--armed={refundArmedId === order.id}
                disabled={refundPendingId === order.id}
                on:click={() => handleRefund(order)}
              >
                {refundPendingId === order.id
                  ? 'Refunding…'
                  : refundArmedId === order.id
                  ? 'Confirm full refund'
                  : 'Refund'}
              </button>
            {/if}
          </div>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .orders {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
  }

  .orders__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .orders h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .orders__summary {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .orders__chips {
    display: flex;
    gap: var(--space-1);
    flex-wrap: wrap;
  }

  .orders__chip {
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
    cursor: pointer;
    text-transform: capitalize;
  }

  .orders__chip--active {
    color: var(--color-text-primary);
    border-color: rgb(var(--accent-rgb) / 0.5);
    background: rgb(var(--accent-rgb) / 0.08);
  }

  .orders ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .orders__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
  }

  .orders__main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .orders__who {
    font-weight: 600;
    font-size: var(--font-size-sm);
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  .orders__status {
    font-size: var(--font-size-2xs);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 1px var(--space-2);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
    color: var(--color-text-muted);
  }

  .orders__status--completed {
    color: var(--color-accent-neon-400);
    border-color: rgba(78, 224, 195, 0.35);
  }

  .orders__status--pending {
    color: var(--color-text-amber);
    border-color: rgb(var(--accent-rgb) / 0.4);
  }

  .orders__status--expired,
  .orders__status--released {
    opacity: 0.7;
  }

  .orders__status--refunded {
    color: var(--color-warning-500);
    border-color: rgba(255, 179, 71, 0.4);
  }

  .orders__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .orders__refund {
    width: auto;
    padding: var(--space-1) var(--space-3);
    font-size: var(--font-size-xs);
  }

  .orders__refund--armed {
    border-color: var(--color-danger-500);
    color: var(--color-danger-500);
  }

  .orders__meta,
  .orders__extras {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .orders__amount {
    font-weight: 600;
    font-size: var(--font-size-sm);
    flex-shrink: 0;
  }

  .orders__empty {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }
</style>
